import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import type {
  UVCoordinates,
  GazePoint,
  DifferentialHeatmapConfig,
  DifferentialHeatmapResult,
} from "../types/gazeHeatmapTypes";
import { directionToEquirectangularUV } from "../utils/coordinateConversion";
import { generateDifferentialHeatmap } from "../utils/differentialHeatmapGeneration";

interface UseDifferentialHeatmapParams {
  /** Gaze points for Group A */
  pointsA: GazePoint[];
  /** Gaze points for Group B */
  pointsB: GazePoint[];
  /** Configuration options */
  config?: Partial<DifferentialHeatmapConfig>;
  /** Whether to generate the heatmap */
  enabled?: boolean;
  /** Scene rotation for UV conversion (Y axis) */
  sceneRotationY?: number;
}

interface UseDifferentialHeatmapResult {
  /** The generated differential heatmap ImageData */
  imageData: ImageData | null;
  /** Generation result with stats */
  result: DifferentialHeatmapResult | null;
  /** Whether the heatmap is being generated */
  isGenerating: boolean;
  /** Error message if generation failed */
  error: string | null;
  /** Regenerate the heatmap */
  regenerate: () => void;
}

/**
 * Hook to generate a differential heatmap comparing two groups
 */
export function useDifferentialHeatmap({
  pointsA,
  pointsB,
  config = {},
  enabled = true,
  sceneRotationY = 0,
}: UseDifferentialHeatmapParams): UseDifferentialHeatmapResult {
  const [imageData, setImageData] = useState<ImageData | null>(null);
  const [result, setResult] = useState<DifferentialHeatmapResult | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generationIdRef = useRef(0);

  // Convert gaze points to UV coordinates
  const convertToUV = useCallback(
    (points: GazePoint[]): UVCoordinates[] => {
      return points.map((p) => directionToEquirectangularUV(p.direction, sceneRotationY));
    },
    [sceneRotationY],
  );

  // Memoize config
  const memoizedConfig = useMemo(() => config, [JSON.stringify(config)]);

  // Generate differential heatmap
  const generate = useCallback(() => {
    if (!enabled || (pointsA.length === 0 && pointsB.length === 0)) {
      setImageData(null);
      setResult(null);
      setIsGenerating(false);
      return;
    }

    generationIdRef.current += 1;
    const currentGenerationId = generationIdRef.current;

    setIsGenerating(true);
    setError(null);

    // Convert to UV coordinates
    const uvPointsA = convertToUV(pointsA);
    const uvPointsB = convertToUV(pointsB);

    // Use requestAnimationFrame to avoid blocking UI
    requestAnimationFrame(() => {
      if (generationIdRef.current !== currentGenerationId) return;

      try {
        const generationResult = generateDifferentialHeatmap(uvPointsA, uvPointsB, memoizedConfig);

        if (generationIdRef.current !== currentGenerationId) return;

        setImageData(generationResult.imageData);
        setResult(generationResult);
        setIsGenerating(false);
      } catch (err) {
        if (generationIdRef.current !== currentGenerationId) return;
        setError(err instanceof Error ? err.message : "Generation failed");
        setIsGenerating(false);
      }
    });
  }, [pointsA, pointsB, memoizedConfig, enabled, convertToUV]);

  // Trigger generation when dependencies change
  useEffect(() => {
    generate();
  }, [generate]);

  return {
    imageData,
    result,
    isGenerating,
    error,
    regenerate: generate,
  };
}
